import { supabase } from '../lib/supabaseClient';
import type { Site, SiteSortKey, SiteFilter, SiteReview } from '../types/sites';

/**
 * 教材サイト一覧を取得
 */
export async function fetchSites(): Promise<Site[]> {
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  
  return data || [];
}

/**
 * 教材サイト一覧を取得（ソート・絞り込み対応）
 */
export async function fetchSitesList(options?: {
  sort?: SiteSortKey;
  filter?: SiteFilter;
  keyword?: string;
  limit?: number;
  offset?: number;
}): Promise<Site[]> {
  let query = supabase
    .from('sites')
    .select(`
      *,
      site_reviews (
        id,
        status,
        is_published,
        rating_place,
        rating_home,
        rating_self,
        rating_print,
        rating_design
      )
    `);

  // キーワード検索
  if (options?.keyword && options.keyword.trim()) {
    const kw = `%${options.keyword.trim()}%`;
    query = query.or(`name.ilike.${kw},description.ilike.${kw},base_url.ilike.${kw}`);
  }

  // フィルター適用
  if (options?.filter) {
    const { freeLevels, tags, minRating } = options.filter;
    
    if (freeLevels && freeLevels.length > 0) {
      query = query.in('free_level', freeLevels);
    }
    
    if (tags && tags.length > 0) {
      query = query.overlaps('tags', tags);
    }
    
    // minRating はフロント側で計算後にフィルタ
  }

  // ソート適用
  switch (options?.sort) {
    case 'newest':
      query = query.order('created_at', { ascending: false });
      break;
    case 'name_asc':
      query = query.order('name', { ascending: true });
      break;
    case 'rating_desc':
    case 'recommended':
    default:
      query = query.order('created_at', { ascending: false });
  }

  // limit/offset
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) throw error;
  
  return data || [];
}

/**
 * slugで教材サイトを1件取得
 */
export async function fetchSiteBySlug(slug: string): Promise<Site | null> {
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  
  return data;
}

/**
 * 全ての教材サイトを取得（管理用）
 */
export async function fetchAllSitesForAdmin(): Promise<Site[]> {
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * 教材サイトを新規作成（管理用）
 */
export async function createSite(payload: Partial<Site>): Promise<Site> {
  const { data, error } = await supabase
    .from('sites')
    .insert([payload])
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * 教材サイトを更新（管理用）
 */
export async function updateSite(id: string, payload: Partial<Site>): Promise<Site> {
  const { data, error } = await supabase
    .from('sites')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * 教材サイトを削除（管理用）
 */
export async function deleteSite(id: string): Promise<void> {
  const { error } = await supabase
    .from('sites')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * 管理者用：教材サイトのステータスを更新
 */
export async function updateSiteStatus(id: string, status: 'draft' | 'published' | 'archived'): Promise<Site> {
  const isActive = status === 'published';
  
  const { data, error } = await supabase
    .from('sites')
    .update({ is_active: isActive, status })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('updateSiteStatus error', error);
    throw error;
  }

  return data;
}

/**
 * 管理者用：AI口コミを一括生成
 * 
 * @param adminUserId 管理者のユーザーID
 * @returns 生成結果(成功件数、スキップ件数、失敗件数)
 */
export async function generateAISiteReviews(adminUserId?: string): Promise<{
  total: number;
  success: number;
  skipped: number;
  failed: number;
  errors?: string[];
}> {
  // adminUserId の必須チェック
  if (!adminUserId) {
    throw new Error('管理者ユーザーIDが指定されていません。ログインしてください。');
  }

  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

  // セッションからアクセストークンを取得
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('認証セッションが見つかりません。再ログインしてください。');
  }

  // 実行前のAI口コミ件数を取得（DB状態確認）
  const { count: beforeCount, error: beforeCountError } = await supabase
    .from('site_reviews')
    .select('*', { count: 'exact', head: true })
    .eq('role', 'AI紹介メモ（自動生成）');

  if (beforeCountError) {
    console.error('[generateAISiteReviews] 実行前のカウント取得エラー', beforeCountError);
  }

  console.log('[generateAISiteReviews] 実行前のAI口コミ件数', beforeCount);

  const functionUrl = `${supabaseUrl}/functions/v1/generate-ai-site-reviews`;

  console.log('[generateAISiteReviews] 呼び出し開始', {
    url: functionUrl,
    adminUserId,
    hasAccessToken: !!session.access_token,
    beforeCount,
  });

  try {
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        admin_user_id: adminUserId,
      }),
    });

    // レスポンスの生テキストを先に取得
    const rawText = await response.text();

    console.log('[generateAISiteReviews] レスポンス受信', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      rawText: rawText.substring(0, 500), // 最初の500文字だけログ
    });

    // 実行後のAI口コミ件数を取得（DB状態確認）
    const { count: afterCount, error: afterCountError } = await supabase
      .from('site_reviews')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'AI紹介メモ（自動生成）');

    if (afterCountError) {
      console.error('[generateAISiteReviews] 実行後のカウント取得エラー', afterCountError);
    }

    const actualCreated = (afterCount || 0) - (beforeCount || 0);

    console.log('[generateAISiteReviews] 実行後のAI口コミ件数', {
      beforeCount,
      afterCount,
      actualCreated,
    });

    if (!response.ok) {
      // JSONパースを試みる
      let errorData: any = {};
      try {
        errorData = JSON.parse(rawText);
      } catch (parseError) {
        console.error('[generateAISiteReviews] JSONパース失敗', parseError);
      }

      const errorMessage = errorData.error || errorData.message || rawText || 'AI口コミの生成に失敗しました';
      
      // DB状態を確認して、実際は一部成功している可能性を通知
      let detailedError = `AI口コミの生成に失敗しました\n\nステータス: ${response.status} ${response.statusText}\nURL: ${functionUrl}\nエラー: ${errorMessage}`;
      
      if (actualCreated > 0) {
        detailedError += `\n\n⚠️ ただし、DBには ${actualCreated} 件のAI口コミが追加されています。\n一部は成功した可能性があります。`;
      }
      
      console.error('[generateAISiteReviews] エラー詳細', {
        status: response.status,
        statusText: response.statusText,
        errorData,
        rawText,
        actualCreated,
      });

      throw new Error(detailedError);
    }

    // 成功時のJSONパース
    let data: any;
    try {
      data = JSON.parse(rawText);
    } catch (parseError) {
      console.error('[generateAISiteReviews] 成功レスポンスのJSONパース失敗', parseError);
      
      // パース失敗でも、DB状態を確認して実際の結果を返す
      if (actualCreated > 0) {
        console.log('[generateAISiteReviews] JSONパース失敗だが、DBには追加されている', actualCreated);
        return {
          total: actualCreated,
          success: actualCreated,
          skipped: 0,
          failed: 0,
          errors: ['レスポンスのパースに失敗しましたが、DBには正常に保存されています'],
        };
      }
      
      throw new Error(`レスポンスのパースに失敗しました: ${rawText.substring(0, 200)}`);
    }

    // レスポンスとDB状態の整合性を確認
    if (actualCreated !== data.success) {
      console.warn('[generateAISiteReviews] レスポンスとDB状態が不一致', {
        responseSuccess: data.success,
        actualCreated,
      });
      
      // DB状態を優先
      data.success = actualCreated;
    }

    console.log('[generateAISiteReviews] 成功', data);

    return data;
  } catch (error) {
    console.error('[generateAISiteReviews] 例外発生', error);
    
    // 例外発生時もDB状態を確認
    const { count: errorAfterCount } = await supabase
      .from('site_reviews')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'AI紹介メモ（自動生成）');

    const actualCreated = (errorAfterCount || 0) - (beforeCount || 0);
    
    if (actualCreated > 0) {
      console.log('[generateAISiteReviews] 例外発生だが、DBには追加されている', actualCreated);
      
      // エラーメッセージに実際の結果を追加
      const originalMessage = error instanceof Error ? error.message : 'AI口コミの生成に失敗しました。';
      const enhancedMessage = `${originalMessage}\n\n⚠️ ただし、DBには ${actualCreated} 件のAI口コミが追加されています。\n一部は成功した可能性があります。`;
      
      throw new Error(enhancedMessage);
    }
    
    throw error;
  }
}

/**
 * サイトの口コミ一覧を取得
 */
export async function fetchSiteReviews(siteId: string): Promise<SiteReview[]> {
  const { data, error } = await supabase
    .from('site_reviews')
    .select('*')
    .eq('site_id', siteId)
    .eq('is_published', true)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * サイトの口コミを投稿
 */
export async function createSiteReview(
  siteId: string,
  payload: {
    role: string;
    title: string;
    body: string;
    rating_place: number;
    rating_home: number;
    rating_self: number;
    rating_print: number;
    rating_design: number;
  }
): Promise<SiteReview> {
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('site_reviews')
    .insert([
      {
        site_id: siteId,
        user_id: user?.id,
        role: payload.role,
        title: payload.title,
        body: payload.body,
        rating_place: payload.rating_place,
        rating_home: payload.rating_home,
        rating_self: payload.rating_self,
        rating_print: payload.rating_print,
        rating_design: payload.rating_design,
        status: 'pending',
        is_published: false,
        helpful_count: 0,
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * 口コミの「役に立った」カウントを増やす
 */
export async function incrementReviewHelpful(reviewId: string): Promise<void> {
  const { error } = await supabase.rpc('increment_site_review_helpful', {
    review_id: reviewId,
  });

  if (error) throw error;
}

/**
 * レシピに紐づくサイトの情報をIDから取得
 */
export async function fetchSiteForRecipeById(id: string) {
  const { data, error } = await supabase
    .from('sites')
    .select('id, slug, name')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('fetchSiteForRecipeById error', error);
    throw error;
  }

  return data;
}

/**
 * ユーザーが投稿したサイト口コミ一覧を取得
 */
export async function fetchSiteReviewsByUser(userId: string): Promise<SiteReview[]> {
  const { data, error } = await supabase
    .from('site_reviews')
    .select(`
      *,
      sites (
        id,
        name,
        slug
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * 承認待ちのサイト口コミ一覧を取得（管理用）
 */
export async function fetchPendingSiteReviews(): Promise<SiteReview[]> {
  const { data, error } = await supabase
    .from('site_reviews')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * 全てのサイト口コミを取得（管理用）
 */
export async function fetchAllSiteReviewsForAdmin(): Promise<SiteReview[]> {
  const { data, error } = await supabase
    .from('site_reviews')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * サイト口コミのステータスを更新（管理用）
 */
export async function moderateSiteReview(
  reviewId: string,
  status: 'approved' | 'rejected'
): Promise<{ data: SiteReview | null; error: any }> {
  const isPublished = status === 'approved';
  
  const { data, error } = await supabase
    .from('site_reviews')
    .update({ 
      status, 
      is_published: isPublished 
    })
    .eq('id', reviewId)
    .select()
    .single();

  return { data, error };
}

/**
 * サイト口コミを削除（管理用）
 */
export async function deleteSiteReview(reviewId: string): Promise<{ error: any }> {
  const { error } = await supabase
    .from('site_reviews')
    .delete()
    .eq('id', reviewId);

  return { error };
}
